import { useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useFirebaseUser } from '../context/FirebaseUserContext';
import { useUserProfile } from '../hooks/useUserProfile';
import { usePaymentProviders } from '../hooks/usePaymentProviders';
import { useSignOut } from '../hooks/useSignOut';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import NoAccessScreen from '../components/NoAccessScreen';

const PublicRoute = ({ children }: { children: React.ReactNode }) => {
    const { user, isLoading: isAuthLoading } = useFirebaseUser();
    const { profile, loading: isProfileLoading } = useUserProfile();
    const { signOut } = useSignOut();
    const signingOutRef = useRef(false);

    // Query payment providers to check if user has stripeSubscription
    const { hasStripeSubscription, loading: isPaymentProvidersLoading, error: paymentProvidersError } = usePaymentProviders(user?.uid);

    // If there was an error loading the profile but user exists, 
    // assume they're a new user and let them continue with signup
    const profileLoadFailed = !!user && !isProfileLoading && !profile;

    // Reset the guard once sign-out completes (user becomes null)
    // so a subsequent profileLoadFailed cycle can trigger sign-out again.
    useEffect(() => {
        if (!user && signingOutRef.current) {
            signingOutRef.current = false;
        }
    }, [user]);

    // Sign out once so the user can restart the signup flow cleanly
    useEffect(() => {
        if (profileLoadFailed && !signingOutRef.current) {
            signingOutRef.current = true;
            signOut('/');
        }
    }, [profileLoadFailed, signOut]);

    // Show loading state while checking authentication, profile, and payment providers
    if (isAuthLoading || isProfileLoading || (user && isPaymentProvidersLoading)) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    // If there's an error loading payment providers, log it
    // For public route, we'll treat it as if user doesn't have subscription
    if (paymentProvidersError) {
        console.error('Error loading payment providers:', paymentProvidersError);
    }

    if (!user) {
        return <>{children}</>; // new user, continue flow
    }

    // Profile not found — sign-out is in progress, show spinner until auth state clears
    if (profileLoadFailed) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    const hasUserId = !!user.uid;

    // If user is signed in but doesn't have stripeSubscription payment provider,
    // show the No Access screen instead of redirecting to profile
    if (user && hasUserId && !hasStripeSubscription) {
        return <NoAccessScreen />;
    }

    // Any logged-in user with stripeSubscription should go to profile instead of signup
    if (user && hasUserId) {
        // Logged-in user accessing signup page - redirecting to profile
        return <Navigate to="/profile" replace />;
    }

    return <>{children}</>; // new user, continue with signup flow
};

export default PublicRoute;